import { createModule, mutation, action } from "vuex-class-component";
import api from "@/plugins/api";
import { auth, bindAsArray, firestore } from "@/plugins/firebase";
import { addDoc, collection, deleteDoc, doc, orderBy, query, serverTimestamp, updateDoc, where } from "firebase/firestore";
import Team, { distance, sheetsDuration, TeamConverter } from "@/domain/team";
import EventMedium, { EventMediumConverter } from "@/domain/event-medium";
import EventBonus from "@/domain/event-bonus";
import Answer, { AnswerConverter } from "@/domain/answer";
import Player, { PlayerConverter } from "@/domain/player";
import { vxm } from "@/store";
import { signInWithCustomToken, signOut } from "firebase/auth";

const VuexModule = createModule({
  namespaced: "results",
  strict: false,
  target: "nuxt"
});

export default class ResultsStore extends VuexModule {
  eventId = "";
  gameId = "";
  scope = "default";
  mediumId = "";
  teams: Team[] = [];
  players: Player[] = [];
  bonuses: EventBonus[] = [];
  answers: Answer[] = [];
  isInstructor = false;
  slideshow = false;

  allMedia: EventMedium[] = [];
  mediaTasksFilter: string[] = [];
  mediaTeamsFilter: string[] = [];
  mediaSortBy = "newest";
  mediaDownloading = false;
  mediaDownloadTotal = 0;
  mediaDownloadTeams = 0;
  mediaDownloadTasks = 0;

  answersDownloading = false;
  standingsDownloading = false;

  unbindBonuses = () => {};
  firestoreSubscriptions: (() => void)[] = [];

  @action
  async bindEvent(id: string) {
    this.gameId = id.split(":")[0];
    this.scope = id.split(":")[1] || "default";
    this.eventId = this.gameId + (this.scope === "default" ? "" : `:${this.scope}`);

    await Promise.all([vxm.games.loadGame(this.gameId), this.bindTeams()]);

    // Media, answers and messages collections depend on teams or game settings
    await Promise.all([this.bindPlayers(), this.bindMedia(), this.bindAnswers()]);
  }

  @action
  async unbind() {
    for (const unsubscribe of this.firestoreSubscriptions) {
      unsubscribe();
    }

    this.firestoreSubscriptions = [];
  }

  @mutation setMediumId(id: string) {
    this.mediumId = id;
  }

  @mutation setIsInstructor(isInstructor: boolean) {
    this.isInstructor = isInstructor;
  }

  @mutation setSlideshow(slideshow: boolean) {
    this.slideshow = slideshow;
  }

  @action
  async checkInstructorAccess() {
    try {
      const { data } = await api.get(`/v3/events/${this.gameId}/token?scope=${this.scope}`);
      await signInWithCustomToken(auth, data.token);
      this.setIsInstructor(true);
    } catch (_err) {
      await signOut(auth);
      this.setIsInstructor(false);
    }
  }

  @action
  async bindTeams() {
    const converter = new TeamConverter();

    await bindAsArray(
      this.$store,
      "teams",
      query(collection(firestore, `events/${this.eventId}/teams`).withConverter(converter), orderBy("totalScore", "desc"))
    );
  }

  @action
  async bindPlayers() {
    const converter = new PlayerConverter(this);
    const subscription = await bindAsArray(
      this.$store,
      "players",
      query(collection(firestore, `events/${this.eventId}/players`).withConverter(converter))
    );

    this.firestoreSubscriptions.push(subscription);
  }

  @action
  async bindAnswers() {
    const converter = new AnswerConverter(this);
    const subscription = await bindAsArray(
      this.$store,
      "answers",
      query(collection(firestore, `events/${this.eventId}/answers`).withConverter(converter), orderBy("time", "asc"))
    );

    this.firestoreSubscriptions.push(subscription);
  }

  @action
  async bindMedia() {
    const converter = new EventMediumConverter();
    const subscription = await bindAsArray(
      this.$store,
      "allMedia",
      query(
        collection(firestore, `events/${this.eventId}/media`).withConverter(converter),
        where("processed", "==", true),
        orderBy("time", "asc")
      )
    );

    this.firestoreSubscriptions.push(subscription);
  }

  @action
  async bindBonuses() {
    this.unbindBonuses();

    this.unbindBonuses = await bindAsArray(
      this.$store,
      "bonuses",
      query(
        collection(firestore, `events/${this.eventId}/bonuses`).withConverter(EventBonus.firestoreConverter),
        where("mediumId", "==", this.mediumId),
        orderBy("time", "desc")
      )
    );
  }

  @action
  async addBonus({ score, comment }: { score: number; comment?: string }) {
    const medium = this.media.find(medium => medium.id === this.mediumId);
    const team = medium && this.teams.find(team => team.id === medium.teamId);

    if (!medium || !team) {
      throw new Error("medium_not_selected");
    }

    try {
      const { data } = await api.post(`/v3/events/${this.eventId}/bonuses`, {
        scope: this.scope,
        teamId: team.id,
        mediumId: this.mediumId,
        score,
        comment
      });
    } catch (err) {
      console.log(err);
    }
  }

  @action
  async deleteBonus(bonusId: string) {
    return await api.delete(`/v3/events/${this.eventId}/bonuses/${bonusId}`);
  }

  @action
  async sendChatMessage({ text, privateTeamIds }: { text: string; privateTeamIds: null | string[] }) {
    if (!vxm.auth.user) {
      throw new Error("not authenticated");
    }

    await addDoc(collection(firestore, `events/${this.eventId}/messages`), {
      type: "WEB",
      time: serverTimestamp(),
      fromInstructorId: vxm.auth.user.id,
      fromInstructorName: vxm.auth.user.name,
      text,
      privateTeamIds,
      privatePlayerIds: null
    });
  }

  @action
  async deleteMessage(id: string) {
    await deleteDoc(doc(firestore, `events/${this.eventId}/messages/${id}`));
  }

  @action
  async deleteMedium() {
    return await api.delete(`/v3/events/${this.eventId}/media/${this.mediumId}`);
  }

  @mutation
  updateMedium(fields: Partial<EventMedium>) {
    const index = this.allMedia.findIndex(m => m.id === this.mediumId);
    if (index !== -1) {
      this.allMedia = [...this.allMedia.slice(0, index), { ...this.allMedia[index], ...fields }, ...this.allMedia.slice(index + 1)];
    }
  }

  @action
  async updateMediumTransform({ rotation, mirrored }: { rotation: number; mirrored: boolean }) {
    const mediumRef = doc(firestore, `events/${this.eventId}/media/${this.mediumId}`);
    const normalizedRotation = ((rotation % 360) + 360) % 360;
    // Update the local state first to provide immediate feedback in the UI
    this.updateMedium({ rotation: normalizedRotation, mirrored });
    await updateDoc(mediumRef, {
      rotation: normalizedRotation,
      mirrored
    });
  }

  /** Game data getters */
  get game() {
    return vxm.games.db[this.gameId];
  }

  get taskIndices() {
    const tasks = vxm.games.db[this.gameId].tasks;
    const indices: { [id: string]: number } = {};

    for (let i = 0; i < tasks.length; ++i) {
      indices[tasks[i].id] = i;
    }

    return indices;
  }

  get taskScores() {
    const tasks = vxm.games.db[this.gameId].tasks;
    const scores: { [id: string]: number } = {};

    for (const task of tasks) {
      scores[task.id] = task.difficulty;
    }

    return scores;
  }

  get teamsById() {
    const map: { [id: string]: Team } = {};

    for (const team of this.teams) {
      map[team.id] = team;
    }

    return map;
  }

  get teamAlphabeticIndices() {
    const indices: { [id: string]: number } = {};
    const teams = this.teams.slice();

    teams.sort((a, b) => {
      if (a.name < b.name) return -1;
      if (a.name > b.name) return 1;
      return 0;
    });

    for (let i = 0; i < teams.length; ++i) {
      indices[teams[i].id] = i;
    }

    return indices;
  }

  get media() {
    let items = this.allMedia;

    if (this.mediaTasksFilter.length) {
      items = items.filter(item => this.mediaTasksFilter.includes(item.taskId));
    }

    if (this.mediaTeamsFilter.length) {
      items = items.filter(item => this.mediaTeamsFilter.includes(item.teamId));
    }

    switch (this.mediaSortBy) {
      case "oldest":
        items = items.slice().reverse();
        break;
      case "highest score":
        items = items.slice();
        items.sort((a, b) => b.score + this.taskScores[b.taskId] - (a.score + this.taskScores[a.taskId]));
        break;
      case "lowest score":
        items = items.slice();
        items.sort((a, b) => a.score + this.taskScores[a.taskId] - (b.score + this.taskScores[b.taskId]));
        break;
      case "task":
        items = items.slice();
        items.sort((a, b) => this.taskIndices[a.taskId] - this.taskIndices[b.taskId]);
        break;
      case "team":
        items = items.slice();
        items.sort((a, b) => this.teamAlphabeticIndices[a.teamId] - this.teamAlphabeticIndices[b.teamId]);
        break;
    }

    return items;
  }

  @action
  async downloadStandings() {
    this.standingsDownloading = true;

    const data: any[][] = this.teams.map(team => [
      team.name,
      team.members ? team.members.join(", ") : "",
      team.isFinished ? "true" : "false",
      team.totalScore,
      team.totalAnswers,
      team.correctAnswers,
      team.incorrectAnswers,
      team.hints,
      distance(team.odometer),
      sheetsDuration(team.time)
    ]);

    const XLSX = await import("xlsx");
    const workbook = XLSX.utils.book_new();
    const sheet = XLSX.utils.aoa_to_sheet(
      [["Team", "Members", "Finished", "Score", "Answers", "Correct", "Incorrect", "Hints", "Distance", "Time"]].concat(data)
    );

    XLSX.utils.book_append_sheet(workbook, sheet, "Standings");
    XLSX.writeFile(workbook, `${this.game.title.replace(/\s/g, "-").toLowerCase()}-standings.xlsx`);

    this.standingsDownloading = false;
  }

  @action
  async downloadAnswers() {
    this.answersDownloading = true;

    const XLSX = await import("xlsx");
    const workbook = XLSX.utils.book_new();
    let taskIndex = 0;

    for (const task of this.game.tasks) {
      const data: any[][] = this.answers
        .filter(answer => answer.taskId === task.id && answer.team)
        .map(answer => [answer.team.name, answer.asString, answer.score, answer.timeToAnswer]);

      const sheet = XLSX.utils.aoa_to_sheet([["Team", "Answer", "Score", "Time spent"]].concat(data));

      XLSX.utils.book_append_sheet(workbook, sheet, `Task ${++taskIndex}`);
    }

    XLSX.writeFile(workbook, `${this.game.title.replace(/\s/g, "-").toLowerCase()}-answers.xlsx`);

    this.answersDownloading = false;
  }
}
